/**
 * product-detail custom element
 *
 * - swatch clicks render section's via Shopify section rendering API
 * - listen for addon selections and update product-form id/qty inputs for adding bundles/addons to cart
 * - @TODO: update document title on swatch change
 * - @TODO: fix selected swatch on history pops
*/

(() => {
  class ProductDetail extends HTMLElement {
    constructor() {
      super();
      this.els();
      this.settings();
      this.listeners();
      this.klaviyoInitBackInStock();
    }

    els() {
      this.addToCartEl = this.querySelector('[data-product-add-to-cart]');
      this.addToCartPriceEl = this.querySelector('[data-product-add-to-cart-price]');
      this.addonInputEls = this.querySelectorAll('[data-product-addon-inputs]');
      this.bundleInputEl = this.querySelector('[data-product-bundle-prop]');
      this.bundledWithInputEl = this.querySelector('[data-product-bundled-with]');
      this.optionLabelEls = this.querySelectorAll('[data-option-label]');
      this.sectionEl = this.closest('.shopify-section');
      this.sectionId = this.sectionEl.id.split('shopify-section-')[1];
      this.zoomEl = document.querySelector('[data-product-media-modal]');
    }

    settings() {
      this.variants = this.querySelector('[data-json="product-variants"]');
      this.variantId = this.querySelector('[name="id"]').id;
      this.isQuickadd = this.closest('quickadd-modal') !== null;

      this.addonData = {
        addons: {
          selectedTotal: 0,
        },
        bundles: {
          selectedTotal: 0,
        },
      };
      this.idsToUpdate = ['pdpBadge', 'pdpGallery', 'pdpTitle', 'price', 'variantRadios', 'productForm', 'pdpAddons', 'pdpSecondary'];
      if (this.addToCartPriceEl) this.price = parseFloat(this.addToCartPriceEl.dataset.productPrice);

      this.variantData = JSON.parse(this.variants.textContent);
      this.uom = this.querySelector('[name="properties[__uom]"]').value;
      this.options = this.querySelector('[name="properties[__options]"]').value.split('|');
      this.uomOptionIndex = this.querySelector('[data-uom-option]').dataset.uomOption;
      this.sizeOptionIndex = this.querySelector('[data-size-option]').dataset.sizeOption;
      this.updateSwatchUrls();
      this.updateInventoryMessages();

      const installmentsEl = this.querySelector('#product-form-installment');
      if (!installmentsEl) return;
      this.productFormInstallment = {
        el: installmentsEl,
      }
      this.productFormInstallment.id = this.productFormInstallment.el.querySelector('[name="id"]');
      this.productFormInstallment.productId = this.productFormInstallment.el.querySelector('[name="product-id"]');
    }

    swatchUnsubscriber = undefined;
    uomChangeUnsubscriber = undefined;
    variantChangeUnsubscriber = undefined;

    connectedCallback() {
      this.addonsUnsubscriber = window.subscribe(PUB_SUB_EVENTS.addonsUpdate, event => {
        this.addonData[event.type] = event;
        this.updateForm(event.data);
      });

      this.swatchUnsubscriber = window.subscribe(PUB_SUB_EVENTS.swatchChange, event => {
        if (event.source !== 'product-detail') return;
        this.loadingStateBegin();
        this.update(event.url);
      });

      this.uomChangeUnsubscriber = window.subscribe(PUB_SUB_EVENTS.uomChange, event => {
        if (event.sectionId !== this.sectionId) return;
        this.uom = event.uom;
        this.onUomChange();
      });

      this.variantChangeUnsubscriber = window.subscribe(PUB_SUB_EVENTS.variantChange, event => {
        if (event.sectionId !== this.sectionId) return;
        this.currentVariant = event.currentVariant;
        this.onVariantChange();
      });
    }

    disconnectedCallback() {
      const unsubscribers = [
        this.swatchUnsubscriber,
        this.uomChangeUnsubscriber,
        this.variantChangeUnsubscriber,
      ];

      unsubscribers.forEach(unsubscriber => unsubscriber && unsubscriber());
    }

    listeners() {
      const onHistoryChange = () => {
        this.update(location.href);
      };

      window.addEventListener('popstate', onHistoryChange);
    }

    addonSelectedIndex(id, selectedData) {
      return selectedData.findIndex(item => item.id === id);
    }

    loadingStateBegin() {
      this.sectionEl.classList.add('pdp--loading');
    }

    loadingStateEnd() {
      this.sectionEl.classList.remove('pdp--loading');
    }

    onUomChange() {
      this.updateSwatchUrls();
      this.updateInventoryMessages();
    }

    onVariantChange() {
      if (this.currentVariant.preorder) {
        this.addToCartEl.dataset.label = 'pre-order';
      } else if (this.currentVariant.backordered) {
        this.addToCartEl.dataset.label = 'backorder';
      } else {
        this.addToCartEl.dataset.label = 'add-to-cart';
      }
    }

    render(html) {
      this.renderDiv = document.createElement('div');
      this.renderDiv.innerHTML = html;
      const sectionEl = this.closest('.shopify-section');
      if (sectionEl) sectionEl.innerHTML = this.renderDiv.querySelector(`.shopify-section--product${this.isQuickadd ? '-quickadd' : ''}`).innerHTML;

      requestAnimationFrame(() => {
        window.loadSwiper();
        window.inView();
        this.updateSwatchUrls();
        if (window.yotpoWidgetsContainer) window.yotpoWidgetsContainer.initWidgets();
        this.initTheLineButton();
      });
    }

    updateZoom(html) {
      const div = document.createElement('div');
      div.innerHTML = html;
      this.zoomEl.innerHTML = div.querySelector(`.shopify-section`).innerHTML;

      requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('productMediaModalInit'));
      });
    }

    initTheLineButton() {
      document.dispatchEvent(new CustomEvent('DOMContentLoaded'));
    }

    updateProductFormInstallment() {
      // this.productFormInstallment.id = newId;
      // this.productFormInstallment.productId = newProductId;
    }

    updateInventoryMessages() {
      // reset option labels
      for (let elem of this.optionLabelEls) {
        elem.classList.remove('out-of-stock');
        elem.classList.remove('backordered');
        elem.classList.remove('preorder');
        elem.querySelector('.low-inventory-msg').classList.add('js-hide');
      }
      this.variantData.forEach((v) => {
        // Option label should be visible on the 2nd of 3 options
        // If 2nd options only has one value it is hidden so it should show on the last option
        const has3VisibleOptions = v.options.length === 3 && v.productOptions[1].values.length > 1;
        const inventoryLabelOption =
          v.options[has3VisibleOptions ? this.options.length - 2 : this.options.length - 1];
        if ((has3VisibleOptions && v[this.uomOptionIndex] == this.uom) || !has3VisibleOptions) {
          if (v.preorder) {
            const labelEl = this.querySelector(`[data-option-label="${inventoryLabelOption}"]`);
            if (labelEl) labelEl.classList.add('preorder');
            return;
          }
          if (v.oos) {
            const labelEl = this.querySelector(`[data-option-label="${inventoryLabelOption}"]`);
            if (labelEl) labelEl.classList.add('out-of-stock');
            return;
          }
          if (v.backordered) {
            const labelEl = this.querySelector(`[data-option-label="${inventoryLabelOption}"]`);
            if (labelEl) labelEl.classList.add('backordered');
            return;
          }
          if (v.lowInventoryMsg) {
            const lowInventoryEl = this.querySelector(
              `[data-option-label="${inventoryLabelOption}"] .low-inventory-msg`
            );
            if (!lowInventoryEl) return;
            lowInventoryEl.innerHTML = v.lowInventoryMsg;
            lowInventoryEl.classList.remove('js-hide');
          }
        }
      })
    }

    refreshPdpBulkOrderDrawer(url) {
      const bulkOrderDrawer = document.querySelector('pdp-bulk-order-drawer');
      const urlWithoutVariantId = url.split('?')[0];

      if (!bulkOrderDrawer) return;
      bulkOrderDrawer.fetchNewContent(urlWithoutVariantId);
      bulkOrderDrawer.setDrawerTrigger();
    }

    update(url) {
      const separator = url.includes('?') ? '&' : '?';
      const fetchUrl = `${url}${separator}sections=${this.sectionId}${this.zoomEl ? ',product-media-modal' : ''}`;

      fetch(fetchUrl)
        .then(response => response.json())
        .then(response => {
          this.render(response[this.sectionId]);
          if (this.zoomEl) this.updateZoom(response['product-media-modal']);
          this.updateUrl(url);
          this.refreshPdpBulkOrderDrawer(url);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.loadingStateEnd();
      });
    }

    updateDetails(html) {
      this.idsToUpdate.forEach(id => {
        const elId = `${id}-${this.sectionId}`;
        const el = this.querySelector(`#${elId}`);
        const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
        const parsedEl = parsedHtml.querySelector(`#${elId}`);
        if (!(el && parsedEl)) return;

        el.innerHTML = parsedEl.innerHTML;
      })

      requestAnimationFrame(() => {
        window.swipers();
        window.inView();
      });
    }

    updateAddToCartButton() {
      this.addToCartPriceEl = this.querySelector('[data-product-add-to-cart-price]');
      if (this.addToCartPriceEl) this.addToCartPriceEl.innerHTML = window.Shopify.formatMoney(this.price + this.addonData.addons.selectedTotal + this.addonData.bundles.selectedTotal).replace('.00', '');
    }

    updateForm(addonsData) {
      const bundleId = Date.now();

      Object.values(addonsData).forEach(item => {
        if (!item || !item.hasOwnProperty('id')) return;
        const el = this.querySelector(`[data-product-addon-inputs="${item.id}"]`);
        const quantityInput = el.querySelector('[data-addon-quantity-input]');
        const idInput = el.querySelector('[data-addon-id-input]');
        const bundleInput = el.querySelector('[data-addon-bundle-input]');
        const bundledWithInput = el.querySelector('[data-addon-bundled-with-input]');

        if (item.isSelected) {
          // if selected add name attribute
          idInput.setAttribute('name', idInput.dataset.name);
          quantityInput.setAttribute('name', quantityInput.dataset.name);

          // add _bundle_id prop to main product and bundled product
          if (bundleInput) {
            bundleInput.setAttribute('name', bundleInput.dataset.name);
            bundleInput.value = bundleId;
            bundledWithInput.setAttribute('name', bundledWithInput.dataset.name);
            this.bundleInputEl.setAttribute('name', this.bundleInputEl.dataset.name);
            this.bundleInputEl.value = bundleId;
            this.bundledWithInputEl.setAttribute('name', this.bundledWithInputEl.dataset.name);
          }

          quantityInput.value = item.quantity;
        } else {
          // if not selected remove name attribute
          idInput.removeAttribute('name');
          quantityInput.removeAttribute('name');
          if (bundleInput) {
            bundleInput.removeAttribute('name');
            bundleInput.removeAttribute('name');
            bundledWithInput.removeAttribute('name');
          }
        }
      });

      // remove _bundle_id_id prop from main product if no bundled products are selected
      const bundledItem = this.querySelector('[name][data-addon-bundle-input]');
      if (!bundledItem) {
        if (this.bundleInputEl) this.bundleInputEl.removeAttribute('name');
        if (this.bundledWithInputEl) this.bundledWithInputEl.removeAttribute('name');
      } else {
        if (this.bundledWithInputEl) this.bundledWithInputEl.value = bundledItem.dataset.title;
      }

      this.updateAddToCartButton(addonsData);
    }

    /*
     * Update swatch URLs to have CTN or EA query param based on current variant
     * UOM should persist when changing swatches
    */
    updateSwatchUrls() {
      this.swatches = this.querySelector('product-swatches');

      if (!this.swatches) return;

      const swatchesTextContent = this.swatches.querySelector('[data-swatches-json]')?.textContent;

      this.swatchData = this.swatches.data || (swatchesTextContent && JSON.parse(swatchesTextContent));
      if (!this.swatchData) return;

      const selectedSizeEl = this.querySelector('[name="Size"]:checked');
      const selectedSize = this.currentVariant ? this.currentVariant[this.sizeOptionIndex] : selectedSizeEl ? selectedSizeEl.value : null;

      this.swatches.querySelectorAll('[data-swatch-item]').forEach(el => {
        const handle = el.dataset.swatchItem;
        const data = this.swatchData[handle];
        const url = new URL(el.href);
        const params = new URLSearchParams(url.search);
        const uomV = data.variants.find(v => v[this.uomOptionIndex] === this.uom);
        const uomAndSizeV = data.variants.find(v => v[this.uomOptionIndex] === this.uom && v[this.sizeOptionIndex] === selectedSize);
        const vId = uomAndSizeV ? uomAndSizeV.id : uomV ? uomV.id : null;
        if (vId) params.set('variant', vId);
        url.search = params.toString();
        el.href = url.toString();
      });
    }

    updateUrl(url) {
      window.history.replaceState({}, '', url)
    }

    klaviyoInitBackInStock() {
    }
  }

  if (!customElements.get('product-detail')) {
    customElements.define('product-detail', ProductDetail);
  }
})();
